import { FocusMonitor } from '@angular/cdk/a11y';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  signal,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ChatStore, PromptGeneratorService } from '@coin/chat-bot-data-access';
import {
  ChatDialogContentComponent,
  ChatDialogFooterComponent,
  ChatDialogHeaderComponent,
  ChatMessageComponent,
  ChatStartViewComponent
} from '@coin/chat-bot-ui';
import { ReferenceDocument, CocoCelebrationService } from '@coin/chat-bot-util-types';
import { ConfirmationDialogComponent } from '@coin/shared/core/ui-storybook';
import { TranslateModule } from '@ngx-translate/core';
import { filter } from 'rxjs';
import { ChatFaqScreenComponent } from '../chat-faq-screen/chat-faq-screen.component';
import { CdkDrag, CdkDragHandle, DragRef, Point } from '@angular/cdk/drag-drop';
import { ResizableDirective } from '../../directives/resizable/resizable.directive';
import { MatIcon } from '@angular/material/icon';
import { SnowflakeComponent } from '../snowflake/snowflake.component';

interface SnowflakeData {
  id: number;
  x: number;
  size: number;
  duration: number;
  delay: number;
  variant: number;
}

@Component({
  selector: 'coin-chat-dialog',
  templateUrl: './chat-dialog.component.html',
  styleUrl: './chat-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ChatDialogHeaderComponent,
    ChatDialogContentComponent,
    ChatDialogFooterComponent,
    ChatMessageComponent,
    ChatStartViewComponent,
    TranslateModule,
    ChatFaqScreenComponent,
    CdkDrag,
    CdkDragHandle,
    ResizableDirective,
    MatIcon,
    SnowflakeComponent
  ],
  providers: [PromptGeneratorService],
  host: {
    '(keydown)': 'handleKeydown($event)'
  }
})
export class ChatDialogComponent {
  private readonly chatBox = viewChild<ElementRef<HTMLDivElement>>('chatBox');
  private readonly chatStore = inject(ChatStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly focusMonitor = inject(FocusMonitor);
  private readonly elRef = inject(ElementRef);
  private readonly promptGeneratorService = inject(PromptGeneratorService);
  private readonly dialog = inject(MatDialog);
  private readonly celebrationManager = inject(CocoCelebrationService);

  messages = this.chatStore.selectSignal(state => state._messages);
  isLoadingAssistantResponse = this.chatStore.selectSignal(state => state._isLoading);
  isActive = true;
  user = this.chatStore.user;
  isSendButtonDisabled = computed(() => this.chatStore.isSendDisabled());
  isFaqScreenActive = signal(false);
  hasPendingMessages = computed<boolean>(() => {
    return this.messages().some(message => !message.finished);
  });
  prompts = this.promptGeneratorService.prompts;

  protected readonly globalAnimation = signal<string | null>(this.celebrationManager.getAnimation());
  protected readonly showBatAnimation = computed(() => this.globalAnimation() === 'bat');
  protected readonly showSnowAnimation = computed(() => this.globalAnimation() === 'snow');
  protected readonly snowflakes = signal<SnowflakeData[]>([]);

  constructor() {
    // Generate snowflakes if winter celebration is active
    if (this.showSnowAnimation()) {
      this.generateSnowflakes();
    }

    // Monitor keyboard
    effect(() => {
      const handler = this.handleKeydown;
      window.addEventListener('keydown', handler);
      return () => window.removeEventListener('keydown', handler);
    });

    // Monitor focus
    this.focusMonitor
      .monitor(this.elRef.nativeElement, true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(origin => {
        this.isActive = !!origin;
      });

    // Monitor click inside chat box
    effect(() => {
      const chatBox = this.chatBox();
      const clickHandler = () => {
        this.isActive = true;
      };
      if (chatBox) {
        chatBox.nativeElement.addEventListener('click', clickHandler);
        return () => chatBox.nativeElement.removeEventListener('click', clickHandler);
      }

      return () => {};
    });
  }

  constrainPosition = (
    userPointerPosition: Point,
    dragRef: DragRef,
    dimensions: DOMRect,
    pickupPositionInElement: Point
  ) => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Calculate top-left position of the dialog based on cursor offset
    let newX = userPointerPosition.x - pickupPositionInElement.x;
    let newY = userPointerPosition.y - pickupPositionInElement.y;

    // Clamp horizontally
    if (newX < 0) newX = 0;
    else if (newX + dimensions.width > viewportWidth) newX = viewportWidth - dimensions.width;

    // Clamp vertically
    if (newY < 0) newY = 0;
    else if (newY + dimensions.height > viewportHeight) newY = viewportHeight - dimensions.height;

    return { x: newX, y: newY };
  };

  closeChat() {
    this.chatStore.toggle();
  }

  sendMessage(content: string) {
    this.chatStore.postUserMessage(content);
  }

  handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && this.isActive && this.isChatActive() && this.chatStore.isOpen()) {
      event.preventDefault();
      this.closeChat();
    }
  }

  private isChatActive(): boolean {
    const focusedEl = document.activeElement;
    return this.chatBox()?.nativeElement.contains(focusedEl);
  }

  downloadFile(document: ReferenceDocument) {
    this.chatStore.downloadFile(document);
  }

  cleanMessages() {
    ConfirmationDialogComponent.openDialog(this.dialog, {
      headline: 'general.confirm',
      translate: true,
      msg: `chat-bot.window.delete-context-message`,
      confirmMsg: 'general.delete',
      cancelMsg: 'general.btnCancel'
    })
      .pipe(filter(Boolean), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.chatStore.clearMessages();
      });
  }

  toggleFaq() {
    this.isFaqScreenActive.update(value => !value);
  }

  private generateSnowflakes(): void {
    // Generate 50 snowflakes with random properties
    // Using fewer snowflakes than React example (50 vs 100) to avoid flooding the dialog
    const flakes: SnowflakeData[] = Array.from({ length: 50 }, (_, i) => ({
      id: i,
      x: Math.random() * 100, // Random horizontal position (0-100%)
      size: Math.random() * 20 + 15, // Random size between 15-35px (smaller than React example)
      duration: Math.random() * 5 + 10, // Random duration between 10-15s (slower)
      delay: Math.random() * 10, // Random delay between 0-10s for staggered start
      variant: Math.floor(Math.random() * 5) + 1 // Random variant 1-5
    }));
    this.snowflakes.set(flakes);
  }
}
